import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseWorkflowSignatureCaptureParams {
  signatureAcknowledged: boolean;
  setSignatureAcknowledged: (acknowledged: boolean) => void;
  clearCapturedWhenUnacknowledged?: boolean;
}

export interface UseWorkflowSignatureCaptureResult {
  capturedSignature: string | null;
  signatureCaptureOpen: boolean;
  signaturePreviewSrc?: string;
  openSignatureCapture: () => void;
  closeSignatureCapture: () => void;
  saveSignature: (signatureBase64Png: string) => void;
  clearSignature: () => void;
  resetSignatureCapture: () => void;
}

export const useWorkflowSignatureCapture = ({
  signatureAcknowledged,
  setSignatureAcknowledged,
  clearCapturedWhenUnacknowledged = false,
}: UseWorkflowSignatureCaptureParams): UseWorkflowSignatureCaptureResult => {
  const [capturedSignature, setCapturedSignature] = useState<string | null>(null);
  const [signatureCaptureOpen, setSignatureCaptureOpen] = useState(false);

  useEffect(() => {
    if (!clearCapturedWhenUnacknowledged || signatureAcknowledged) {
      return;
    }

    setCapturedSignature((previous) => (previous ? null : previous));
  }, [clearCapturedWhenUnacknowledged, signatureAcknowledged]);

  const signaturePreviewSrc = useMemo(
    () => (capturedSignature ? `data:image/png;base64,${capturedSignature}` : undefined),
    [capturedSignature],
  );

  const openSignatureCapture = useCallback(() => {
    setSignatureCaptureOpen(true);
  }, []);

  const closeSignatureCapture = useCallback(() => {
    setSignatureCaptureOpen(false);
  }, []);

  const saveSignature = useCallback(
    (signatureBase64Png: string) => {
      setCapturedSignature(signatureBase64Png);
      setSignatureAcknowledged(true);
      setSignatureCaptureOpen(false);
    },
    [setSignatureAcknowledged],
  );

  const clearSignature = useCallback(() => {
    setCapturedSignature(null);
    setSignatureAcknowledged(false);
  }, [setSignatureAcknowledged]);

  const resetSignatureCapture = useCallback(() => {
    setCapturedSignature(null);
    setSignatureCaptureOpen(false);
    setSignatureAcknowledged(false);
  }, [setSignatureAcknowledged]);

  return {
    capturedSignature,
    signatureCaptureOpen,
    signaturePreviewSrc,
    openSignatureCapture,
    closeSignatureCapture,
    saveSignature,
    clearSignature,
    resetSignatureCapture,
  };
};
